import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import Modal from '@Components/Shared/Modals/Modal';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import BidHistoryTable from '@Components/Shared/BidHistoryTable';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const BidHistoryModal = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedBid, setSelectedBid] = useState(null);
    const [selectedMaxBidValue, setSelectedMaxBidValue] = useState(null);
    const [preBidHistory, setPreBidHistory] = useState(() => props.bidHistory.PreBidHistory);
    const [showDeleteBidConfirmation, setShowDeleteBidConfirmation] = useState(false);
    const [showSaveMaxBidConfirmation, setShowSaveMaxBidConfirmation] = useState(false);
    const currentSite = useRecoilValue(atoms.currentWebsite);

    const confirmDeleteBid = (bid) => {
        setSelectedBid(bid);
        setShowDeleteBidConfirmation(true);
    };

    const cancelDeleteBid = () => {
        setSelectedBid(null);
        setShowDeleteBidConfirmation(false);
    };

    const confirmSaveMaxBid = (bid, maxBidValue) => {
        // Convert bid amount from a formatted currency string to a number
        const currentBidAmount = Number(String(bid.BidAmount).replace(/[^0-9.-]+/g, ''));

        if (maxBidValue < 1 || currentBidAmount === Number(maxBidValue)) {
            return;
        }

        setSelectedBid(bid);
        setSelectedMaxBidValue(maxBidValue);
        setShowSaveMaxBidConfirmation(true);
    };

    const cancelSaveMaxBid = () => {
        setShowSaveMaxBidConfirmation(false);
    };

    const deleteBid = () => {
        if (!isLoading) {
            setShowDeleteBidConfirmation(false);
            setIsLoading(true);
            sandhillsRequest({
                url: routes.AdminDeletePreBid,
                data: {
                    listingId: props.bidHistory.ListingID,
                    centralUserAuthId: selectedBid.BidderCentralUserAuthID
                },
                method: 'post',
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                if (data) {
                    if (data.Success == true) {
                        selectedBid.IsDeleted = true;
                    }
                    else {
                        alert('An error occurred while deleting the bid. ' + data.Message);
                    }
                }
                else {
                    alert('An error occurred while deleting the bid. Please refresh your page and try again.');
                }
            }).catch((error) => {
                alert('An error occurred while deleting the bid. Please refresh your page and try again.');
            }).finally(() => {
                setSelectedBid(null);
                setIsLoading(false);
            });
        }
    };

    const saveMaxBid = () => {
        if (!isLoading && selectedMaxBidValue) {
            setShowSaveMaxBidConfirmation(false);
            setIsLoading(true);
            sandhillsRequest({
                url: routes.AdminUpdatePreBid,
                data: {
                    listingId: props.bidHistory.ListingID,
                    centralUserAuthId: selectedBid.BidderCentralUserAuthID,
                    newMaxBid: selectedMaxBidValue
                },
                method: 'post',
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                if (data && data.Success == true) {
                    // Our save request was successful. Now do another server request to refresh the client-side state with the updated bid history.
                    return sandhillsRequest({
                        url: routes.GetBidHistory.replace("{0}", props.bidHistory.ListingID),
                        errorHandling: {
                            useConsole: false,
                            useAlert: false
                        }
                    }).then((updatedBidHistory) => {
                        if (updatedBidHistory) {
                            setPreBidHistory(updatedBidHistory.PreBidHistory);
                        }
                        else {
                            throw new Error('Bid history refresh failed.');
                        }
                    });
                }
                else {
                    alert('An error occurred while updating the max bid.');
                }
            }).catch((error) => {
                alert('An error occurred while updating the max bid. Please refresh your page and try again.');
            }).finally(() => {
                setSelectedBid(null);
                setSelectedMaxBidValue(null);
                setIsLoading(false);
            });
        }
    };

    return (
        <Modal containerClassName='bid-history-modal' onEscapeKeyClose={props.onClose}>
            <Modal.Header onClose={props.onClose}>
                <Modal.Title>
                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.bidhistoryemployeeview' />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {showDeleteBidConfirmation ?
                    <>
                        <div className="delete-bid-confirmation">
                            <div>
                                Are you sure you want to delete the {selectedBid.BidAmount} pre-bid for bidder #{selectedBid.BidderNumber} ({selectedBid.BidderName})?
                            </div>
                            <div>
                                <Button className='delete-bid-confirm-btn' variant="contained" onClick={(e) => { deleteBid(); }}><TranslationText tKey='sandhills.yes' /></Button>
                                <Button className='delete-bid-cancel-btn' variant="contained" onClick={(e) => { cancelDeleteBid(); }}><TranslationText tKey='sandhills.no' /></Button>
                            </div>
                        </div>
                    </>
                    : showSaveMaxBidConfirmation ?
                    <>
                        <div className="save-max-bid-confirmation">
                            <div>
                                Are you sure you want to update the max bid for bidder #{selectedBid.BidderNumber} ({selectedBid.BidderName}) to {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(selectedMaxBidValue))}?
                            </div>
                            <div>
                                <Button className='save-max-bid-confirm-btn' variant="contained" onClick={(e) => { saveMaxBid(); }}><TranslationText tKey='sandhills.yes' /></Button>
                                <Button className='save-max-bid-cancel-btn' variant="contained" onClick={(e) => { cancelSaveMaxBid(); }}><TranslationText tKey='sandhills.no' /></Button>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div>
                            <div>
                                <h2><TranslationText tKey='sandhills.sellerinformation' /></h2>
                                <div>
                                    <div>
                                        <strong className='inline-header'><TranslationText tKey='sandhills.companyname' />:</strong>
                                        {props.bidHistory.SellerName}
                                    </div>
                                    <div>
                                        <strong className='inline-header'><TranslationText tKey='sandhills.location' />:</strong>
                                        {props.bidHistory.SellerLocation}
                                    </div>
                                    <div>
                                        <strong className='inline-header'><TranslationText tKey='sandhills.phone' />:</strong>
                                        {props.bidHistory.SellerPhone}
                                    </div>
                                    <div>
                                        <strong className='inline-header'><TranslationText tKey='email' />:</strong>
                                        {props.bidHistory.SellerEmail}
                                    </div>
                                    {props.bidHistory.AuctionEndDate &&
                                        <div>
                                            <strong className='inline-header'><TranslationText tKey='sandhills.auctionenddate' />:</strong>
                                            {props.bidHistory.AuctionEndDate}
                                        </div>}
                                </div>
                            </div>
                            {(props.bidHistory.IsAuctionResult || currentSite.IsAuctionTimeAuctionSite) &&
                                <BidHistoryTable
                                    bidHistory={props.bidHistory.LiveBidHistory}
                                    deleteBid={null}
                                    showMaxBids={props.bidHistory.ShowMaxBids}
                                    showEditMaxBids={false}
                                    saveMaxBid={null}
                                    tableTitle={translator.translate('sandhills.livebids').toString()}
                                />}
                            {!currentSite.IsAuctionTimeAuctionSite &&
                                <BidHistoryTable
                                    bidHistory={preBidHistory}
                                    deleteBid={props.bidHistory.IsAuctionResult ? null : confirmDeleteBid}
                                    showMaxBids={props.bidHistory.ShowMaxBids}
                                    showEditMaxBids={props.bidHistory.ShowEditMaxBids}
                                    saveMaxBid={confirmSaveMaxBid}
                                    tableTitle={translator.translate('sandhills.prebids').toString()}
                                />}

                        </div>
                    </>
                }
                <Backdrop
                    open={isLoading}
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress />
                </Backdrop>
            </Modal.Body>
        </Modal>
    );
};

export default BidHistoryModal;